import * as PropTypes from 'prop-types';

import {AnnotationAttributes} from 'biojs/Annotation';
import {PrimerPairAttributes} from 'biojs/PrimerPair';
import {SequenceAnalysisSummary} from 'biojs/Sequence';
import {SequencePrimerAttributes} from 'biojs/SequencePrimer';
import {CRISPRAnalysisSummary} from 'constants/CRISPRConstants';
import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import {User} from 'constants/UserConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {NotebookSectionColor} from 'graphql-client/__generated/graphqlGlobalTypes';
import {GenericRunOutputSamplesTable} from 'lab-automation/output-samples/RunOutputSamplesTableConstants';
import {GenericDataTable, GenericDataTableConfig} from 'notebook/data-tables/DataTableConstants';
import {
  ClientId,
  EntrySummary,
  FromServerId,
  NLOId,
  ProtocolSummary,
  Selector,
  ToServerId,
} from 'notebook/ELNConstants';
import {GenericNoteLinkedEmbedding} from 'notebook/embeddings/NoteLinkedEmbeddingUtil';
import {GenericNoteLinkedAppCanvas} from 'notebook/integrations/benchling-apps/NoteLinkedAppCanvasUtil';
import {GenericNoteLinkedChart} from 'notebook/integrations/charts/NoteLinkedChartPlugin';
import {GenericPlateRecord} from 'notebook/integrations/plate-maps/plate-record/NoteLinkedPlateRecordUtil';
import {GenericMixtureIngredientsStructuredTable} from 'notebook/integrations/registry/MixtureIngredientsStructuredTableAdapter';
import {GenericMixtureMetadataStructuredTable} from 'notebook/integrations/registry/MixtureMetadataStructuredTableAdapter';
import {GenericRegistrationStructuredTable} from 'notebook/integrations/registry/RegistrationStructuredTableAdapter';
import {GenericResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {GenericBoxCreationStructuredTable} from 'notebook/integrations/samples/BoxCreationStructuredTableAdapter';
import {GenericInventoryStructuredTable} from 'notebook/integrations/samples/InventoryStructuredTableAdapter';
import {GenericPlateCreationStructuredTable} from 'notebook/integrations/samples/PlateCreationStructuredTableAdapter';
import {GenericTransferStructuredTable} from 'notebook/integrations/samples/TransferStructuredTableAdapter';
import {Format, formatType} from 'notebook/mediocre/formatted-text/FormattersConstants';
import {BaseNoteLinkedGroup} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import type {BaseNoteLinkedData} from 'notebook/notes/note-linked-objects/NoteLinkedDataUtil';
import {BaseNoteFillInTextBox} from 'notebook/notes/plugins/fill-in-text-box/constants/FillInTextBoxConstants';
import {
  GenericRegistrationTable,
  GenericRegistrationTableConfig,
} from 'notebook/registration-tables/RegistrationTableConstants';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import * as TableConstants from 'notebook/tables/TableConstants';
import {BaseNoteRuntimeSnapshot, NoteRuntimeSnapshot} from 'notebook/tables/TableStoreTransforms';
import {TFIFileMeta} from 'notebook/tfi/TFIConstants';
import {
  GenericTransferTableConfig,
  GenericTransferTableModel,
} from 'notebook/transfer-tables/TransferTableConstants';
import {GenericProcessOutputStructuredTable} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import {GenericProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import {GenericNoteLinkedAssayData} from 'runs/AttachmentConstants';
import {TrayTask} from 'tray/TrayTaskConstants';

export interface ProxiedNotePropTypes {
  cid: string;
  fileId: string;
  indentation: number;
  index: number;
  locked: boolean;
  viewer?: boolean;
}
export const PROXIED_NOTE_PROP_TYPES = {
  cid: PropTypes.string.isRequired,
  fileId: PropTypes.string.isRequired,
  indentation: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  locked: PropTypes.bool.isRequired,
  viewer: PropTypes.bool,
};

export enum NotePartType {
  TEXT = 'text',
  TABLE = 'table',
  CODE = 'code',
  TEXT_BOX = 'text_box',
}

export enum NoteLinkedObjectType {
  DATA_TABLE = 'data_table',
  DATA_TABLE_CONFIG = 'data_table_config',
  NOTE_LINKED_ASSAY_DATA = 'note_linked_assay_data',
  NOTE_LINKED_CHART = 'note_linked_chart',
  NOTE_LINKED_FILE = 'note_linked_file',
  NOTE_LINKED_GROUP = 'note_linked_group',
  NOTE_LINKED_DATA = 'note_linked_data',
  NOTE_LINKED_PLATE_DESIGN = 'note_linked_plate_design',
  NOTE_LINKED_PLATE_RECORD = 'note_linked_plate_transfer_record',
  NOTE_LINKED_APP_CANVAS = 'note_linked_app_canvas',
  NOTE_LINKED_EMBEDDING = 'note_linked_embedding',
  NOTE_FILL_IN_TEXT_BOX = 'note_fill_in_text_box',
  REGISTRATION_TABLE = 'registration_table',
  REGISTRATION_TABLE_CONFIG = 'registration_table_config',
  STRUCTURED_TABLE = 'structured_table',
  TRANSFER_TABLE = 'transfer_table',
  TRANSFER_TABLE_CONFIG = 'transfer_table_config',
}

export interface NoteDay {
  index: number;
  date: string;
  cid: string;
  provenanceUuid: string;
  title?: string;
  sectionColor?: NotebookSectionColor | null;
}

type BaseNote<TIds> = {
  isProtected?: boolean;
  noteParts: Array<BaseNotePart<TIds>>;
  creator: object;
  selectors: Array<Selector>;
  noteRuntimeSnapshot: BaseNoteRuntimeSnapshot<TIds> | null;
  structuredTableMappings?: Array<StructuredTableMapping>;
} & TIds;

// This corresponds to the NoteVersion database model.
export type ServerNote<TIds> = BaseNote<TIds> & {
  created_at: string;
  modified_at: string;
  parentId: number | null;
  days?: never;
  noteRuntimeSnapshot: BaseNoteRuntimeSnapshot<TIds> | null;
};

// The NoteVersion from the server is transformed before being put in the store.
// This corresponds to the outcome of that transformation. See NoteStoreTransform.prepareForStore.
export type BioentityNote = BaseNote<ClientId> & {
  created_at: string;
  latestVersionId: number;
  modified_at: string;
  parentId: number | null;
  days?: never;
  noteRuntimeSnapshot: NoteRuntimeSnapshot;
  focusedTableProvenanceUuid?: string;
  focusedTableViewMode?: TableConstants.FocusedTableViewMode;
};

export type BioentityServerNote = ServerNote<FromServerId> & {
  latestVersionId: number;
  noteRuntimeSnapshot?: BaseNoteRuntimeSnapshot<FromServerId> | null;
};

export type BioentityServerNoteVersion = Omit<ServerNote<FromServerId>, 'noteRuntimeSnapshot'>;

export type EntryNote = BaseNote<ClientId> & {
  days: Array<NoteDay>;
  noteRuntimeSnapshot: NoteRuntimeSnapshot;
  isProtected?: boolean;
};
export type EntryServerNote = BaseNote<FromServerId>;

export type Note = BioentityNote | EntryNote;
export type FromServerNote = BioentityServerNote | EntryServerNote;

export type BaseNotePart<TIds> = {
  text: string;
  table: TableConstants.Table | null;
  formats?: Format;
  important: boolean;
  type: string;
  alignment?: 'left' | 'right' | 'center' | null;
  item_id: string | number | null;
  item: BaseNotePartItem<TIds> | null;
  code: string | null;
  // modified_at, created_at and creator can be null if we haven't yet synced with the server
  modified_at?: string;
  created_at?: string;
  creator?: User;
  indentation: number;
  placeholder: boolean;
  itemWidth: number | null;
  listType: string | null;
  headerType: string | null;
  itemLink: string | null;
  task?: TrayTask;
  taskId?: string;
  provenanceId?: number;
} & TIds;

interface InitialNotePartText {
  type: 'text';
  text: string;
  indentation: number;
  formats: Format;
  listType: string | null;
  headerType: string | null;
}

interface InitialNotePartTable {
  type: 'table';
  table: TableConstants.Table;
}

export type InitialNotePart = InitialNotePartText | InitialNotePartTable;

export type NotePart = BaseNotePart<ClientId>;
export type ToServerNotePart = BaseNotePart<ToServerId>;
export type ServerNotePart = BaseNotePart<FromServerId>;

export type BaseNotePartWithItem<TIds, T extends BaseNotePartItem<TIds>> = BaseNotePart<TIds> & {item: T};
export type NotePartWithItem<T extends BaseNotePartItem<ClientId> = NotePartItem> = BaseNotePartWithItem<
  ClientId,
  T
>;
export type ServerNotePartWithItem<T extends BaseNotePartItem<FromServerId>> = BaseNotePartWithItem<
  FromServerId,
  T
>;

export function isNotePartWithItem(notePart: NotePart): notePart is NotePartWithItem<NotePartItem> {
  return (notePart as NotePartWithItem<NotePartItem>).item != null;
}

export type BaseNotePartItem<TIds> =
  | SequenceAnalysisSummary
  | CRISPRAnalysisSummary
  | NoteLinkedAnnotation
  | NoteLinkedPrimerPair
  | NoteLinkedSequencePrimer
  | NoteLinkedFolderItem
  | BaseNoteLinkedObject<TIds>;
export type NoteLinkedFolderItem = (BioentitySummary | EntrySummary | ProtocolSummary) & {iconId?: string};
export type NotePartItem = BaseNotePartItem<ClientId>;
export type NoteLinkedAnnotation = AnnotationAttributes & {
  sequenceLength: number;
  sequenceStartIndex: number;
};
export type NoteLinkedPrimerPair = PrimerPairAttributes & {
  primer_fwd: SequencePrimerAttributes;
  primer_rev: SequencePrimerAttributes;
  sequence_circular: boolean;
  sequence_length: number;
};
export type NoteLinkedSequencePrimer = SequencePrimerAttributes & {
  sequenceLength: number;
  sequenceStartIndex: number;
};

export type GenericStructuredTable<TIds> =
  | GenericInventoryStructuredTable<TIds>
  | GenericMixtureMetadataStructuredTable<TIds>
  | GenericMixtureIngredientsStructuredTable<TIds>
  | GenericPlateCreationStructuredTable<TIds>
  | GenericRegistrationStructuredTable<TIds>
  | GenericResultsTable<TIds>
  | GenericTransferStructuredTable<TIds>
  | GenericRunOutputSamplesTable<TIds>
  | GenericBoxCreationStructuredTable<TIds>
  | GenericProcessTaskStructuredTable<TIds>
  | GenericProcessOutputStructuredTable<TIds>;

export type AnyStructuredTable = GenericStructuredTable<ClientId>;

export type BaseNoteLinkedObject<TIds> =
  | GenericDataTable<TIds>
  | GenericDataTableConfig<TIds>
  | GenericNoteLinkedAssayData<TIds>
  | GenericNoteLinkedChart<TIds>
  | GenericNoteLinkedFile<TIds>
  | BaseNoteLinkedGroup<TIds, unknown>
  | BaseNoteLinkedData<TIds, unknown>
  | GenericPlateRecord<TIds>
  | GenericNoteLinkedAppCanvas<TIds>
  | GenericNoteLinkedEmbedding<TIds>
  | BaseNoteFillInTextBox<TIds>
  | GenericRegistrationTable<TIds>
  | GenericRegistrationTableConfig<TIds>
  | GenericStructuredTable<TIds>
  | GenericTransferTableModel<TIds>
  | GenericTransferTableConfig<TIds>;

export type NoteLinkedObject = BaseNoteLinkedObject<ClientId>;

export type FromServerNoteLinkedObject = BaseNoteLinkedObject<FromServerId>;

interface BaseNoteLinkedFile {
  name: string;
  type: NoteLinkedObjectType.NOTE_LINKED_FILE;
  tfiFileMeta: TFIFileMeta | null;
  externalFileExcerpt: ExternalFileExcerpt | null;
}

type GenericNoteLinkedFile<TIds> = BaseNoteLinkedFile & {
  excerptType: string | null;
  externalFile: ExternalFile | null;
  previewFile: ExternalFile | null;
  sourcePreviewFile: ExternalFile | null;
  missingPreviewWarning: string | null;
  isPreviewTransformed: boolean | null;
  edits?: string;
} & NLOId<TIds>;

export type NoteLinkedFile = GenericNoteLinkedFile<ClientId>;
export type FromServerNoteLinkedFile = GenericNoteLinkedFile<FromServerId>;

export type ToServerNoteLinkedFile = BaseNoteLinkedFile & NLOId<ToServerId>;

export interface ExternalFile {
  id: string;
  availablePreviewTypes: Array<EXTERNAL_FILE_TYPE>;
  previewType: EXTERNAL_FILE_TYPE | null;
  url: string;
}
export interface ExternalFileExcerpt {
  id: string;
  excerptFile: ExternalFile;
  referenceOffset: number;
}
const notePartType = PropTypes.shape({
  text: PropTypes.string,
  cid: PropTypes.string.isRequired,
  table: TableConstants.table,
  formats: formatType,
  important: PropTypes.bool,
  type: PropTypes.string.isRequired,
  alignment: PropTypes.string,
  item_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  item: PropTypes.object,
});

export const noteType = PropTypes.shape({
  noteParts: PropTypes.arrayOf(notePartType).isRequired,
  cid: PropTypes.string.isRequired,
  creator: PropTypes.object.isRequired,
});
